import { logger } from '@shared-services/log-service';
import { getCollectionAPI } from 'client/widgets/runtime-collection-search/collectionService';
import { CollectionsAPI } from 'client/types';
import {
    createOneOfFiltersData,
    extractCustomFieldId,
    extractOptionId,
} from '.';
import {
    FilterFacetsMap,
    createFilterFacetsMap,
    getFilterFacetsAggregationParams,
} from './filterFacetsMapper';
import { CatalogCustomField, FilterableField, FilterSource } from './types';

const fetchCustomFields = async (
    api: CollectionsAPI
): Promise<CatalogCustomField[]> => {
    const response = await (api as any)
        .storeData('catalog_custom_fields')
        .where('type', 'eq', 'PLAIN_TEXT')
        .get();
    return response?.results ?? [];
};

const enhanceProductOption = (
    field: FilterableField,
    facetsMap: FilterFacetsMap
): FilterableField | null => {
    const optionId = extractOptionId(field.fieldId);
    const option = facetsMap.options?.[optionId];

    if (!option) {
        return null;
    }

    return {
        ...field,
        fieldDisplayName: option.displayName ?? field.fieldDisplayName,
        filtersData: option.buckets
            ? createOneOfFiltersData(Object.keys(option.buckets))
            : field.filtersData,
    };
};

const enhanceCustomField = (
    field: FilterableField,
    customFieldsById: Map<string, CatalogCustomField>
): FilterableField | null => {
    const customFieldId = extractCustomFieldId(field.fieldId);
    const customField = customFieldsById.get(customFieldId);

    if (!customField?.values?.length) {
        return null;
    }

    return {
        ...field,
        fieldDisplayName: customField.name ?? field.fieldDisplayName,
        filtersData: createOneOfFiltersData(customField.values),
    };
};

const fetchFacets = async (filterableFields: FilterableField[]) => {
    try {
        const api = await getCollectionAPI();

        if (!api) {
            return filterableFields;
        }

        api.enableAggregatePreview();
        const aggregationParams = getFilterFacetsAggregationParams();
        const aggregationResult = await api.executeAggregation(
            aggregationParams
        );
        const facetsMap = createFilterFacetsMap(aggregationResult);

        const hasCustomFields = filterableFields.some(
            (field) => field.source === FilterSource.CUSTOM_FIELDS
        );
        const customFields = hasCustomFields
            ? await fetchCustomFields(api)
            : [];
        const customFieldsById = new Map(
            customFields.map((field) => [field.id, field])
        );

        return filterableFields
            .map((field) => {
                if (field.source === FilterSource.PRODUCT_OPTIONS) {
                    return enhanceProductOption(field, facetsMap);
                }
                if (field.source === FilterSource.CUSTOM_FIELDS) {
                    return enhanceCustomField(field, customFieldsById);
                }
                return field;
            })
            .filter((field): field is FilterableField => field !== null);
    } catch (error) {
        logger.warn({
            message: 'Error in filterable fields with facets',
            tags: ['__new-runtime__'],
            error,
        });
        return filterableFields;
    }
};

export default fetchFacets;
